// src/admin/FormEditor/components/FormAvailabilityPeriod.jsx
// Manages the form's availability period with date and time restrictions
// Features:
// - Sets valid_from and valid_to datetime for form availability
// - Validates that valid_to is after valid_from
// - Allows clearing of dates
// - Combines date and time inputs into single datetime string
// Format: "YYYY-MM-DD HH:mm:ss"

import { X } from "lucide-react";
import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateFormMetadata } from "../store/formEditorSlice";
import { prepareNewFormMetadata } from "../utils/formTemplates";

const FormAvailabilityPeriod = memo(() => {
  const dispatch = useDispatch();
  const formMetadata = useSelector((state) => state.formEditor.formMetadata);

  const defaultFormMetaData = prepareNewFormMetadata("Event");

  const handleValidFromDateChange = (e) => {
    const date = e.target.value;
    const time = formMetadata.valid_from?.time?.split(" ")[1] || "00:00:00";
    const newValidFrom = date ? `${date} ${time}` : null;

    if (formMetadata.valid_to?.time && newValidFrom) {
      if (new Date(formMetadata.valid_to.time) <= new Date(newValidFrom)) {
        alert("Valid To must be later than Valid From");
        return;
      }
    }

    const updates = {
      valid_from: {
        ...formMetadata.valid_from,
        time: newValidFrom,
      },
    };
    dispatch(updateFormMetadata({ updates }));
  };

  const handleValidFromTimeChange = (e) => {
    const time = e.target.value;
    const date =
      formMetadata.valid_from?.time?.split(" ")[0] ||
      new Date().toISOString().split("T")[0];
    const newValidFrom = time ? `${date} ${time}` : null;

    if (formMetadata.valid_to?.time && newValidFrom) {
      if (new Date(formMetadata.valid_to.time) <= new Date(newValidFrom)) {
        alert("Valid From must be earlier than Valid To");
        return;
      }
    }

    const updates = {
      valid_from: {
        ...formMetadata.valid_from,
        time: newValidFrom,
      },
    };
    dispatch(updateFormMetadata({ updates }));
  };

  const handleValidToDateChange = (e) => {
    const date = e.target.value;
    const time = formMetadata.valid_to?.time?.split(" ")[1] || "23:59:59";
    const newValidTo = date ? `${date} ${time}` : null;

    if (formMetadata.valid_from?.time && newValidTo) {
      if (new Date(newValidTo) <= new Date(formMetadata.valid_from.time)) {
        alert("Valid To must be later than Valid From");
        return;
      }
    }

    const updates = {
      valid_to: {
        ...formMetadata.valid_to,
        time: newValidTo,
        msg: formMetadata.valid_to?.msg || {},
      },
    };
    dispatch(updateFormMetadata({ updates }));
  };

  const handleValidToTimeChange = (e) => {
    const time = e.target.value;
    const date =
      formMetadata.valid_to?.time?.split(" ")[0] ||
      new Date().toISOString().split("T")[0];
    const newValidTo = time ? `${date} ${time}` : null;

    if (formMetadata.valid_from?.time && newValidTo) {
      if (new Date(newValidTo) <= new Date(formMetadata.valid_from.time)) {
        alert("Valid To must be later than Valid From");
        return;
      }
    }

    const updates = {
      valid_to: {
        ...formMetadata.valid_to,
        time: newValidTo,
        msg: formMetadata.valid_to?.msg || {},
      },
    };
    dispatch(updateFormMetadata({ updates }));
  };

  return (
    <>
      <style>
        {`
          input[type="time"]::-webkit-calendar-picker-indicator {
            cursor: pointer;
          }
          input[type="time"]::-webkit-datetime-edit-fields-wrapper {
            padding: 0;
          }
          /* Increase dropdown height */
          input[type="time"]::-webkit-list-button {
            height: 400px !important;
          }
          /* Target the actual dropdown list */
          input[type="time"]::-webkit-datetime-edit-hour-field:focus,
          input[type="time"]::-webkit-datetime-edit-minute-field:focus,
          input[type="time"]::-webkit-datetime-edit-second-field:focus {
            background-color: #e3f2fd;
          }
        `}
      </style>

      <div className="space-y-1">
        <div className="text-sm font-medium text-gray-700">
          Form Availability Period
        </div>
        <p className="text-sm text-gray-500">
          Leave empty for no time restriction. The form will be available for
          submission only between these dates and times.
        </p>
      </div>

      {/* Valid From */}
      <div className="space-y-1">
        <label className="block text-sm text-gray-600">Valid From</label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={formMetadata.valid_from?.time?.split(" ")[0] || ""}
            onChange={handleValidFromDateChange}
            className="w-44 border rounded-md p-2"
          />
          <input
            type="time"
            step="1"
            value={formMetadata.valid_from?.time?.split(" ")[1] || ""}
            onChange={handleValidFromTimeChange}
            className="w-40 border rounded-md p-2"
            style={{ colorScheme: "light" }}
          />
          <button
            type="button"
            onClick={() =>
              dispatch(
                updateFormMetadata({
                  updates: {
                    valid_from: {
                      time: null,
                      msg: formMetadata.valid_from.msg,
                    },
                  },
                })
              )
            }
            className="text-gray-500 hover:text-red-500 transition-colors"
            title="Clear date and time"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Valid From Message */}
      <div className="space-y-1">
        <label className="block text-sm text-gray-600">
          Valid From Message
        </label>
        {formMetadata.supported_languages.map((lang) => (
          <input
            key={lang}
            type="text"
            value={formMetadata.valid_from?.msg?.[lang] || ""}
            onChange={(e) => {
              const updates = {
                valid_from: {
                  ...formMetadata.valid_from,
                  msg: {
                    ...formMetadata.valid_from?.msg,
                    [lang]: e.target.value,
                  },
                },
              };
              dispatch(updateFormMetadata({ updates }));
            }}
            placeholder={`Message (${lang.toUpperCase()})`}
            className="w-full border rounded-md p-2 mt-1"
          />
        ))}
      </div>

      {/* Valid To */}
      <div className="space-y-1">
        <label className="block text-sm text-gray-600">Valid To</label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={formMetadata.valid_to?.time?.split(" ")[0] || ""}
            min={formMetadata.valid_from?.time?.split(" ")[0] || ""}
            onChange={handleValidToDateChange}
            className="w-44 border rounded-md p-2"
          />
          <input
            type="time"
            step="1"
            value={formMetadata.valid_to?.time?.split(" ")[1] || ""}
            onChange={handleValidToTimeChange}
            className="w-40 border rounded-md p-2"
            style={{ colorScheme: "light" }}
          />
          <button
            type="button"
            onClick={() =>
              dispatch(
                updateFormMetadata({
                  updates: {
                    valid_to: { time: null, msg: formMetadata.valid_to.msg },
                  },
                })
              )
            }
            className="text-gray-500 hover:text-red-500 transition-colors"
            title="Clear date and time"
          >
            <X size={20} />
          </button>
        </div>

        {/* Valid To Messages */}
        <div className="space-y-2">
          <label className="block text-sm text-gray-600">
            Custom "Expired" Message
          </label>
          {formMetadata.supported_languages.map((lang) => (
            <input
              key={lang}
              type="text"
              value={formMetadata.valid_to?.msg?.[lang] || ""}
              onChange={(e) => {
                const updates = {
                  valid_to: {
                    ...formMetadata.valid_to,
                    time: formMetadata.valid_to?.time,
                    msg: {
                      ...formMetadata.valid_to?.msg,
                      [lang]: e.target.value,
                    },
                  },
                };
                dispatch(updateFormMetadata({ updates }));
              }}
              placeholder={`Message (${lang.toUpperCase()})`}
              className="w-full border rounded-md p-2"
            />
          ))}
        </div>
      </div>
    </>
  );
});

export default FormAvailabilityPeriod;
